import axios from 'axios';
import { axiosPrivate } from '@api/ApiHelper/axiosPrivate';
import { pedirIdToken } from '@src/shared/utils/appBridge';
import type { ModoPagina } from '@src/shared/utils/consent';
import type { HistorialClinico, VistaExpediente } from './Consent';

/**
 * Las llamadas de la página del expediente (`/consents/:id` del server). Cada una lleva la
 * credencial del modo: la sesión de la clienta (ID token fresco de la app) o el token corto del
 * link del personal. El server decide qué puede hacer cada quien (`requireConsentAccess`).
 */
export type ModoConAcceso = Exclude<ModoPagina, { modo: 'puente' }>;

const cabeceras = async (modo: ModoConAcceso): Promise<Record<string, string>> =>
    modo.modo === 'app' ? { Authorization: `Bearer ${await pedirIdToken()}` } : { 'X-Staff-Token': modo.staffToken };

/** El mensaje del server viene en lenguaje de clienta (`ConsentError`); lo demás, genérico. */
export const mensajeDeError = (error: unknown): string => {
    if (axios.isAxiosError(error)) {
        const msg = (error.response?.data as { error?: unknown } | undefined)?.error;
        if (typeof msg === 'string' && msg) return msg;
        if (!error.response) return 'Sin conexión. Revisa tu internet e intenta de nuevo.';
    }
    if (error instanceof Error && error.message) return error.message;
    return 'Algo salió mal. Intenta de nuevo en un momento.';
};

export const estadoDeError = (error: unknown): number | undefined =>
    axios.isAxiosError(error) ? error.response?.status : undefined;

const ruta = (id: string, resto = '') => `/consents/${encodeURIComponent(id)}${resto}`;

export const ConsentApi = {
    obtener: async (modo: ModoConAcceso, id: string): Promise<VistaExpediente> => {
        const { data } = await axiosPrivate.get(ruta(id), { headers: await cabeceras(modo) });
        return data as VistaExpediente;
    },

    guardarHistorial: async (modo: ModoConAcceso, id: string, historial: HistorialClinico): Promise<void> => {
        await axiosPrivate.put(ruta(id, '/clinical'), historial, { headers: await cabeceras(modo) });
    },

    firmarConsentimiento: async (modo: ModoConAcceso, id: string, firma: string): Promise<VistaExpediente> => {
        const { data } = await axiosPrivate.post(
            ruta(id, '/sign'),
            { signature: firma },
            { headers: await cabeceras(modo) }
        );
        return data as VistaExpediente;
    },

    firmarRenglon: async (
        modo: ModoConAcceso,
        id: string,
        orderId: string,
        datos: { firma: string; desechable: string; pigmento: string }
    ): Promise<VistaExpediente> => {
        const { data } = await axiosPrivate.post(
            ruta(id, `/procedures/${encodeURIComponent(orderId)}/sign`),
            {
                signature: datos.firma,
                disposable: datos.desechable,
                pigment: datos.pigmento,
                clinicalConfirmed: true
            },
            { headers: await cabeceras(modo) }
        );
        return data as VistaExpediente;
    },

    agregarObservacion: async (
        modo: ModoConAcceso,
        id: string,
        orderId: string,
        texto: string
    ): Promise<VistaExpediente> => {
        const { data } = await axiosPrivate.post(
            ruta(id, `/procedures/${encodeURIComponent(orderId)}/notes`),
            { texto },
            { headers: await cabeceras(modo) }
        );
        return data as VistaExpediente;
    },

    /** El PDF no tiene URL pública: se baja con la credencial y se abre desde memoria. */
    pdf: async (modo: ModoConAcceso, id: string): Promise<Blob> => {
        const { data } = await axiosPrivate.get(ruta(id, '/pdf'), {
            headers: await cabeceras(modo),
            responseType: 'blob'
        });
        return data as Blob;
    }
};
